/**
 * @file DataPointSubCategoryForm.js
 * @description Add or edit a Data Point Sub-Category. Route param subCategoryId === 'new' for creation.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './DataPoints.module.scss';
import { ROUTE_BUILDERS } from '../../constants/routes';
import {
    createDataPointSubCategory,
    updateDataPointSubCategory,
} from '../../services/individualService';
import { getText } from '../../i18n';

const isNew = id => !id || id === 'new';

const DataPointSubCategoryForm = () => {
    const { categoryId, subCategoryId } = useParams();
    const navigate = useNavigate();
    const creating = isNew(subCategoryId);

    const [form, setForm] = useState({ name: '', code: '', enabled: true });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(!creating);
    const [isSaving, setIsSaving] = useState(false);

    const backToList = () =>
        navigate(ROUTE_BUILDERS.dataPointsSubCategory(categoryId));

    useEffect(() => {
        if (creating) return;
        const loadSubCategory = async () => {
            setIsLoading(true);
            try {
                const tenantId = +window.localStorage.getItem('tenantId');
                const { individualApiHttp } =
                    await import('../../services/httpService');
                const response = await individualApiHttp.get(
                    `/v2/individual/tenant/${tenantId}/data-point-subcategory/${subCategoryId}`,
                );
                const item = response.data;
                setForm({
                    name: item.name,
                    code: item.code,
                    enabled: item.enabled ?? true,
                });
            } catch {
                Toast.error(getText('msg_2b70b2ca84ee'));
                backToList();
            } finally {
                setIsLoading(false);
            }
        };
        loadSubCategory();
    }, [subCategoryId]);

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = getText('msg_d7659e2c4af2');
        if (!form.code.trim()) errs.code = getText('msg_ea6ac41a6ab6');
        return errs;
    };

    const handleSave = async () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setIsSaving(true);
        try {
            if (creating) {
                await createDataPointSubCategory({
                    name: form.name.trim(),
                    code: form.code.trim(),
                    dataPointCategoryId: Number(categoryId),
                });
                Toast.success(getText('msg_0f4c43948aaf'));
            } else {
                await updateDataPointSubCategory({
                    id: Number(subCategoryId),
                    name: form.name.trim(),
                    code: form.code.trim(),
                    dataPointCategoryId: Number(categoryId),
                    enabled: form.enabled,
                });
                Toast.success(getText('msg_0da0f2d86c77'));
            }
            backToList();
        } catch {
            Toast.error(getText('msg_31af11d49fd2'));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body text-center mt-5">
                        <Spinner width={50} className={styles.loadingSpinner} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0">
                    <div className="d-flex justify-content-between align-items-center">
                        <p className="page-title float-start ms-4">
                            {creating
                                ? getText('msg_f4b53dff0c01')
                                : getText('msg_2eff1f6cf2c7')}
                        </p>
                    </div>

                    <form
                        className="p-3"
                        style={{ width: '567px', margin: 'auto' }}
                        onSubmit={e => e.preventDefault()}
                    >
                        <div className="form-group mb-3">
                            <label htmlFor="dp-subcat-form-name">
                                {getText('msg_709a23220f2c')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                id="dp-subcat-form-name"
                                type="text"
                                maxLength={128}
                                autoFocus
                                className={classNames('form-control', {
                                    'is-invalid': errors.name,
                                })}
                                value={form.name}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))
                                }
                            />
                            {errors.name && (
                                <div className="invalid-feedback">
                                    {errors.name}
                                </div>
                            )}
                        </div>

                        <div className="form-group mb-3">
                            <label htmlFor="dp-subcat-form-code">
                                {getText('msg_adac69379a62')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                id="dp-subcat-form-code"
                                type="text"
                                maxLength={128}
                                disabled={!creating}
                                className={classNames('form-control', {
                                    'is-invalid': errors.code,
                                })}
                                value={form.code}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        code: e.target.value,
                                    }))
                                }
                            />
                            {errors.code && (
                                <div className="invalid-feedback">
                                    {errors.code}
                                </div>
                            )}
                        </div>
                    </form>

                    <div className="text-center mt-2">
                        <span
                            type="button"
                            onClick={backToList}
                            className="ing-text-buttons d-inline me-3"
                        >
                            <i className="bi bi-x me-2" />
                            {getText('msg_77dfd2135f4d')}
                        </span>
                        <span
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="ing-text-buttons d-inline ms-3"
                        >
                            <i className="bi bi-check-lg me-2" />
                            {getText('msg_efc007a393f6')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataPointSubCategoryForm;
